'use client'
import { __awaiter } from '../../_virtual/_tslib.js';
import { PublicKey } from '@solana/web3.js';
import { findWalletBookWallet } from '@dynamic-labs/wallet-book';
import { isMobile, template } from '@dynamic-labs/utils';
import { SolanaWalletConnector, SolanaUiTransaction } from '@dynamic-labs/solana-core';
import { SolProviderHelper } from '../solProviderHelper.js';
import { SignMessageNotSupportedError } from '../errors/SignMessageNotSupportedError.js';

class InjectedWalletBase extends SolanaWalletConnector {
    getSolProviderHelper() {
        if (!this.wallet) {
            this.wallet = findWalletBookWallet(this.walletBook, this.key);
        }
        if (this.wallet && !this.solProviderHelper) {
            this.solProviderHelper = new SolProviderHelper(this.wallet);
        }
        return this.solProviderHelper;
    }
    getProvider() {
        var _a;
        return (_a = this.getSolProviderHelper()) === null || _a === void 0 ? void 0 : _a.getInstalledProvider();
    }
    setupEventListeners() {
        var _a;
        (_a = this.getSolProviderHelper()) === null || _a === void 0 ? void 0 : _a._setupEventListeners(this);
    }
    teardownEventListeners() {
        var _a;
        (_a = this.getSolProviderHelper()) === null || _a === void 0 ? void 0 : _a._teardownEventListeners();
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            yield ((_a = this.getSolProviderHelper()) === null || _a === void 0 ? void 0 : _a.connect());
        });
    }
    getSigner() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            return (_a = this.getSolProviderHelper()) === null || _a === void 0 ? void 0 : _a.connect();
        });
    }
    createUiTransaction(from) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.validateActiveWallet(from);
            const transaction = new SolanaUiTransaction({
                connection: this.getWalletClient(),
                from,
                onSubmit: (transaction) => __awaiter(this, void 0, void 0, function* () {
                    var _a;
                    if (!transaction)
                        return;
                    const signer = yield this.getSigner();
                    if (!signer)
                        throw new Error('Signer not found');
                    const blockhash = yield this.getWalletClient().getLatestBlockhash();
                    if ('version' in transaction) {
                        transaction.message.recentBlockhash =
                            blockhash.blockhash;
                    }
                    else {
                        const userAddress = yield this.getAddress();
                        if (!userAddress)
                            throw new Error('User address not found');
                        transaction.recentBlockhash = blockhash.blockhash;
                        transaction.feePayer =
                            (_a = transaction.feePayer) !== null && _a !== void 0 ? _a : new PublicKey(userAddress);
                    }
                    return (yield signer.signAndSendTransaction(transaction)).signature;
                }),
            });
            return transaction;
        });
    }
    isInstalledOnBrowser() {
        var _a;
        return Boolean((_a = this.getSolProviderHelper()) === null || _a === void 0 ? void 0 : _a.isInstalledHelper());
    }
    getAddress() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            if (!this.isInstalledOnBrowser()) {
                if (isMobile() &&
                    ((_a = this.wallet) === null || _a === void 0 ? void 0 : _a.mobile) &&
                    this.wallet.mobile.inAppBrowser &&
                    this.mobileExperience === 'in-app-browser') {
                    const inAppBrowserCompiledTemplate = template(this.wallet.mobile.inAppBrowser);
                    const deepLink = inAppBrowserCompiledTemplate({
                        encodedDappURI: encodeURIComponent(window.location.toString()),
                    });
                    window.location.assign(deepLink);
                }
                return;
            }
            return (_b = this.getSolProviderHelper()) === null || _b === void 0 ? void 0 : _b.getAddress();
        });
    }
    signMessage(messageToSign) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            const walletAddress = yield ((_a = this.getSolProviderHelper()) === null || _a === void 0 ? void 0 : _a.getAddress());
            if (walletAddress && this.isLedgerAddress(walletAddress)) {
                throw new SignMessageNotSupportedError(this.name);
            }
            return (_b = this.getSolProviderHelper()) === null || _b === void 0 ? void 0 : _b.signMessage(messageToSign);
        });
    }
    getConnectedAccounts() {
        return __awaiter(this, void 0, void 0, function* () {
            var _a, _b;
            return (_b = (_a = this.getSolProviderHelper()) === null || _a === void 0 ? void 0 : _a.getConnectedAccounts()) !== null && _b !== void 0 ? _b : [];
        });
    }
}

export { InjectedWalletBase };
